import { Button } from "@chakra-ui/react";
import React from "react";
import {
  BsCalendar,
  BsChevronLeft,
  BsChevronRight,
  BsCoin,
  BsEnvelope,
  BsGift,
  BsPeople,
  BsStar,
  BsTelephone,
} from "react-icons/bs";
import { FaCrown } from "react-icons/fa";
import Loading from "../components/common/ui/Loading";
import { getChainRecords } from "../utils/api";
import useStore from "../utils/store";
import "./Chain.css";

const Chain = () => {
  const [data, setData] = React.useState({});
  const limit = 50;
  const total = data?.totalPages * limit;
  const [loading, setLoading] = React.useState(true);
  const [page, setPage] = React.useState(1);
  const getAllData = async () => {
    setLoading(true);
    const [success, data] = await getChainRecords(page);
    if (success) {
      window.scrollTo(0, 0);

      setData(data);
    }
    setLoading(false);
  };
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getInitials = (name) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };
  const user = useStore((state) => state.user);
  const referralData = data;
  React.useEffect(() => {
    getAllData();
  }, [page]);

  return user?.getPurchaseRecords ? (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className="chain-container">
          <div className="chain-content">
            {/* Header */}
            <div className="chain-header">
              <div>
                <h1 className="chain-title">Referral Dashboard</h1>
                <p className="chain-subtitle">
                  Manage and track your referral network
                </p>
              </div>

              {/* Pagination */}
              <div className="pagination-controls">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                >
                  <BsChevronLeft className="chevron-icon" />
                  Previous
                </Button>
                <span className="pagination-text">
                  Page {referralData.currentPage} of {referralData.totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= referralData.totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  Next
                  <BsChevronRight className="chevron-icon" />
                </Button>
              </div>
            </div>

            {/* Stats Overview */}

            {/* Referral Chains */}
            <div className="referral-chains">
              {referralData.referrals.map((referral, index) => (
                <div key={referral._id} className="referral-card">
                  <div className="referral-header">
                    <div className="referral-header-content">
                      <h3 className="referral-title">
                        <BsPeople className="referral-icon" />
                        Referral Chain #{index + 1}
                      </h3>
                      <div className="referral-date">
                        <BsCalendar className="date-icon" />
                        {formatDate(referral.createdAt)}
                      </div>
                    </div>
                  </div>

                  <div className="referral-content">
                    <div className="referral-list">
                      {referral.list.map((item, userIndex) => (
                        <div key={item._id}>
                          <div className="user-profile-container">
                            {/* User Profile */}
                            <div className="user-profile">
                              <div className="user-avatar">
                                <img
                                  src={item.user.image || "/placeholder.svg"}
                                  alt={item.user.name}
                                  className="avatar-image"
                                />
                                <div className="avatar-fallback">
                                  {getInitials(item.user.name)}
                                </div>
                              </div>

                              <div className="user-info">
                                <div className="user-name-section">
                                  <h3 className="user-name">
                                    {item.user.name}
                                  </h3>
                                  {item.user.isReferred ? (
                                    <span className="badge secondary">
                                      Referred User
                                    </span>
                                  ) : (
                                    <span className="badge primary">
                                      Referrer
                                    </span>
                                  )}
                                </div>

                                <div className="user-details">
                                  <div className="detail-item">
                                    <BsEnvelope className="detail-icon" />
                                    <span className="detail-text">
                                      {item.user.email}
                                    </span>
                                  </div>
                                  <div className="detail-item">
                                    <BsTelephone className="detail-icon" />
                                    <span className="detail-text">
                                      {item.user.phoneNumber}
                                    </span>
                                  </div>
                                  <div className="detail-item">
                                    <BsGift className="detail-icon" />
                                    <span className="detail-text">
                                      Code: {item.user.uniqueCode}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Subscription Status */}
                            <div className="subscription-status">
                              <div className="status-header">
                                <FaCrown className="status-icon" />
                                <span className="status-label">
                                  Subscription Status
                                </span>
                              </div>

                              <div className="status-items">
                                <div className="status-item">
                                  <span className="status-text">Regular</span>
                                  <span
                                    className={`badge ${
                                      item.user.isSubscriptionValid
                                        ? "primary"
                                        : "secondary"
                                    }`}
                                  >
                                    {item.user.isSubscriptionValid
                                      ? "Active"
                                      : "Inactive"}
                                  </span>
                                </div>

                                <div className="status-item">
                                  <span className="status-text">Premium</span>
                                  <span
                                    className={`badge ${
                                      item.user.isPremiumSubscriptionValid
                                        ? "primary"
                                        : "secondary"
                                    }`}
                                  >
                                    {item.user.isPremiumSubscriptionValid
                                      ? "Active"
                                      : "Inactive"}
                                  </span>
                                </div>

                                <div className="status-item">
                                  <span className="status-text">Count</span>
                                  <span className="status-value">
                                    {item.subscriptionCount}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Rewards & Coins */}
                            <div className="rewards-section">
                              <div className="rewards-header">
                                <BsCoin className="rewards-icon" />
                                <span className="rewards-label">Rewards</span>
                              </div>

                              <div className="rewards-items">
                                <div className="reward-item">
                                  <span className="reward-text">
                                    Total Coins
                                  </span>
                                  <span className="reward-value total">
                                    {item.user.totalCoin || 0}
                                  </span>
                                </div>

                                <div className="reward-item">
                                  <span className="reward-text">
                                    Referral Coins
                                  </span>
                                  <span className="reward-value">
                                    {item.user.referralCoin || 0}
                                  </span>
                                </div>

                                {item.user.lockedReferralCoin && (
                                  <div className="reward-item">
                                    <span className="reward-text">
                                      Locked Coins
                                    </span>
                                    <span className="reward-value locked">
                                      {item.user.lockedReferralCoin}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Subscription Dates */}
                          {item.user.subscriptionBuyDate && (
                            <div className="subscription-dates">
                              <div className="dates-grid">
                                <div className="date-item">
                                  <span className="date-label">
                                    Subscription Start:
                                  </span>
                                  <span className="date-value">
                                    {formatDate(item.user.subscriptionBuyDate)}
                                  </span>
                                </div>
                                <div className="date-item">
                                  <span className="date-label">
                                    Subscription End:
                                  </span>
                                  <span className="date-value">
                                    {formatDate(
                                      item.user.subscriptionExpireDate
                                    )}
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}

                          {userIndex < referral.list.length - 1 && (
                            <div className="referral-divider">
                              <div className="divider-content">
                                <div className="divider-line"></div>
                                <BsStar className="divider-icon" />
                                <span className="divider-text">REFERRED</span>
                                <BsStar className="divider-icon" />
                                <div className="divider-line"></div>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Bottom Pagination */}
            <div className="bottom-pagination">
              <div className="pagination-controls">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                >
                  <BsChevronLeft className="chevron-icon" />
                  Previous
                </Button>
                <span className="pagination-text">
                  Page {referralData.currentPage} of {referralData.totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= referralData.totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  Next
                  <BsChevronRight className="chevron-icon" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  ) : (
    <p style={{ textAlign: "center", color: "red", padding: "20px" }}>
      You don't have permission to access this page
    </p>
  );
};

export default Chain;
